import {parseRemoteState, type RemoteState} from './remote-room';

export type RemoteStatus = 'local' | 'connecting' | 'connected' | 'error';

/**
 * The seam. Views talk to this and never to a socket, a URL or a poll timer,
 * so polling can be swapped for something chattier later without touching a
 * single view.
 */
export interface RemoteTransport {
  publish(state: RemoteState): void;
  onState(handler: (state: RemoteState) => void): () => void;
  onStatus(handler: (status: RemoteStatus, detail: string) => void): () => void;
  close(): void;
}

type PollingOptions = {
  deckId: string;
  room: string;
  fetchImpl?: typeof fetch;
  intervalMs?: number;
  now?: () => number;
  schedule?: (run: () => void, ms: number) => number;
  cancel?: (handle: number) => void;
  isHidden?: () => boolean;
};

/**
 * Plain HTTPS, one request a second.
 *
 * Deliberately not a socket: a dropped request is not an event here, it is just
 * a second where nothing arrived, and the next tick recovers on its own. That
 * matters more than latency on a dropzone's wifi, and a slide that lands a
 * second late is a slide that landed.
 */
export class PollingRemoteTransport implements RemoteTransport {
  #deckId: string;
  #room: string;
  #fetch: typeof fetch;
  #intervalMs: number;
  #schedule: (run: () => void, ms: number) => number;
  #cancel: (handle: number) => void;
  #isHidden: () => boolean;
  #handle = 0;
  #closed = false;
  #seenRevision = 0;
  #stateHandlers = new Set<(state: RemoteState) => void>();
  #statusHandlers = new Set<(status: RemoteStatus, detail: string) => void>();
  #status: RemoteStatus = 'connecting';
  #detail = '';
  #pending: RemoteState | null = null;
  #sending = false;
  #localVersion = 0;

  constructor(options: PollingOptions) {
    this.#deckId = options.deckId;
    this.#room = options.room;
    this.#fetch = options.fetchImpl ?? ((...args) => fetch(...args));
    this.#intervalMs = options.intervalMs ?? 1000;
    this.#schedule = options.schedule ?? ((run, ms) => setTimeout(run, ms) as unknown as number);
    this.#cancel = options.cancel ?? ((handle) => clearTimeout(handle));
    this.#isHidden = options.isHidden ?? (() => typeof document !== 'undefined' && document.hidden);
    this.#tick();
  }

  get status(): RemoteStatus {
    return this.#status;
  }

  publish(state: RemoteState): void {
    if (this.#closed) return;
    this.#localVersion += 1;
    this.#pending = {...state};
    void this.#drain();
  }

  onState(handler: (state: RemoteState) => void): () => void {
    this.#stateHandlers.add(handler);
    return () => this.#stateHandlers.delete(handler);
  }

  onStatus(handler: (status: RemoteStatus, detail: string) => void): () => void {
    this.#statusHandlers.add(handler);
    handler(this.#status, this.#detail);
    return () => this.#statusHandlers.delete(handler);
  }

  close(): void {
    this.#closed = true;
    this.#pending = null;
    this.#cancel(this.#handle);
    this.#setStatus('local', '');
  }

  /** One write in flight; rapid navigation keeps only the latest queued state. */
  async #drain(): Promise<void> {
    if (this.#sending || this.#closed) return;
    this.#sending = true;
    try {
      while (this.#pending && !this.#closed) {
        const state = this.#pending;
        this.#pending = null;
        if (!(await this.#send(state))) {
          this.#pending ??= state;
          break;
        }
      }
    } finally {
      this.#sending = false;
    }
  }

  async #send(state: RemoteState): Promise<boolean> {
    try {
      const response = await this.#fetch(this.#url(), {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({deckId: this.#deckId, state}),
      });
      if (this.#closed) return true;
      if (!response.ok) {
        this.#setStatus('error', `Servern svarade ${response.status}`);
        return false;
      }
      const body = (await response.json()) as {revision?: number};
      // Our own write is already on screen. Remembering its revision is what
      // stops the next poll from handing it back to us as news.
      if (Number.isSafeInteger(body.revision)) this.#seenRevision = Math.max(this.#seenRevision, body.revision as number);
      this.#setStatus('connected', '');
      return true;
    } catch (cause) {
      if (!this.#closed) this.#setStatus('error', describe(cause));
      return false;
    }
  }

  #tick(): void {
    if (this.#closed) return;
    void this.#drain().then(() => this.#poll()).finally(() => {
      if (this.#closed) return;
      this.#handle = this.#schedule(() => this.#tick(), this.#intervalMs);
    });
  }

  async #poll(): Promise<void> {
    // A tab nobody is looking at has nothing to show and no reason to ask.
    if (this.#isHidden() || this.#closed || this.#sending || this.#pending) return;
    const localVersion = this.#localVersion;
    try {
      const response = await this.#fetch(`${this.#url()}?since=${this.#seenRevision}`, {
        headers: {'Cache-Control': 'no-cache'},
      });
      if (this.#closed || localVersion !== this.#localVersion) return;
      if (response.status === 204) {
        this.#setStatus('connected', '');
        return;
      }
      if (!response.ok) {
        this.#setStatus('error', `Servern svarade ${response.status}`);
        return;
      }
      const body = (await response.json()) as {revision?: number; deckId?: string; state?: unknown};
      if (this.#closed || localVersion !== this.#localVersion) return;
      const state = parseRemoteState(body.state);
      // An older revision cannot win, and another deck's position is not ours.
      if (!state || !Number.isSafeInteger(body.revision)) return;
      if ((body.revision as number) <= this.#seenRevision) return;
      if (body.deckId !== this.#deckId) return;
      this.#seenRevision = body.revision as number;
      this.#setStatus('connected', '');
      for (const handler of this.#stateHandlers) handler(state);
    } catch (cause) {
      if (!this.#closed) this.#setStatus('error', describe(cause));
    }
  }

  #url(): string {
    return `/api/room/${encodeURIComponent(this.#room)}`;
  }

  #setStatus(status: RemoteStatus, detail: string): void {
    if (status === this.#status && detail === this.#detail) return;
    this.#status = status;
    this.#detail = detail;
    for (const handler of this.#statusHandlers) handler(status, detail);
  }
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : 'Ingen kontakt';
}
